const DB_NAME = "certificador_db";
const DB_VERSION = 1;
const STORE_NAME = "signatures";

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "id",
        });

        store.createIndex("status", "status", { unique: false });
        store.createIndex("type", "type", { unique: false });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
  });
}

function createId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `signature_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function toPlainObject(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeSignature(payload) {
  const plainPayload = toPlainObject(payload || {});
  const now = new Date().toISOString();

  return {
    id: plainPayload.id || createId(),
    schemaVersion: plainPayload.schemaVersion || 1,
    type: plainPayload.type || "visual",
    status: plainPayload.status || "active",

    signer: {
      name: plainPayload.signer?.name || "",
      role: plainPayload.signer?.role || "",
      registry: plainPayload.signer?.registry || "",
      document: plainPayload.signer?.document || "",
      email: plainPayload.signer?.email || "",
    },

    image: {
      mime: plainPayload.image?.mime || "image/png",
      dataUrl: plainPayload.image?.dataUrl || "",
      width: Number(plainPayload.image?.width || 0),
      height: Number(plainPayload.image?.height || 0),
      transparent: plainPayload.image?.transparent !== false,
    },

    placement: {
      page: plainPayload.placement?.page || "front",
      topMm: Number(plainPayload.placement?.topMm || 0),
      leftMm: Number(plainPayload.placement?.leftMm || 0),
      widthMm: Number(plainPayload.placement?.widthMm || 0),
      heightMm: Number(plainPayload.placement?.heightMm || 0),
      zIndex: Number(plainPayload.placement?.zIndex || 5),
    },

    digital: {
      enabled: Boolean(plainPayload.digital?.enabled),
      provider: plainPayload.digital?.provider || null,
      certificateAlias: plainPayload.digital?.certificateAlias || null,
      validationMode: plainPayload.digital?.validationMode || null,
    },

    createdAt: plainPayload.createdAt || now,
    updatedAt: now,
  };
}

function runTransaction(mode, callback) {
  return openDatabase().then((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const store = transaction.objectStore(STORE_NAME);

      transaction.oncomplete = () => {
        db.close();
      };

      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };

      transaction.onabort = () => {
        db.close();
        reject(transaction.error);
      };

      try {
        callback(store, resolve, reject);
      } catch (error) {
        db.close();
        reject(error);
      }
    });
  });
}

export async function getSignatures() {
  return runTransaction("readonly", (store, resolve, reject) => {
    const request = store.getAll();

    request.onsuccess = () => {
      const signatures = request.result || [];

      resolve(
        signatures.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        })
      );
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export async function getSignatureById(id) {
  return runTransaction("readonly", (store, resolve, reject) => {
    const request = store.get(id);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export async function saveSignature(payload) {
  const signature = normalizeSignature(payload);

  return runTransaction("readwrite", (store, resolve, reject) => {
    let request;

    try {
      request = store.put(signature);
    } catch (error) {
      reject(error);
      return;
    }

    request.onsuccess = () => {
      resolve(signature);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export async function deleteSignature(id) {
  return runTransaction("readwrite", (store, resolve, reject) => {
    const request = store.delete(id);

    request.onsuccess = () => {
      resolve(true);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export async function clearSignatures() {
  return runTransaction("readwrite", (store, resolve, reject) => {
    const request = store.clear();

    request.onsuccess = () => {
      resolve(true);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export async function exportSignatures() {
  const signatures = await getSignatures();

  return {
    app: "Certificador",
    module: "signatures",
    schemaVersion: 1,
    exportedAt: new Date().toISOString(),
    items: signatures,
  };
}

export async function importSignatures(items = []) {
  const imported = [];

  for (const item of items) {
    const saved = await saveSignature({
      ...item,
      id: item.id || createId(),
    });

    imported.push(saved);
  }

  return imported;
}
